import React, { useState } from 'react';
import { LoadingButton, Modal, useStateContext } from '@veda/dashboard-ui';
import { usePppApi } from '../services/pppApi';

const STATO_CFG = {
  non_avviata: {
    key:       'ppp_fase_non_avviata',
    dot:       'bg-gray-200',
    border:    'border-gray-200',
    bg:        'bg-gray-50',
    text:      'text-gray-500',
    badge:     'bg-gray-100 text-gray-500',
    connector: 'bg-gray-200',
    label:     'text-gray-400',
  },
  in_corso: {
    key:       'ppp_fase_in_corso',
    dot:       'bg-green-500',
    border:    'border-green-400',
    bg:        'bg-green-50',
    text:      'text-green-800',
    badge:     'bg-green-100 text-green-700',
    connector: 'bg-green-300',
    label:     'text-green-700 font-semibold',
  },
  conclusa: {
    key:       'ppp_fase_conclusa',
    dot:       'bg-green-600',
    border:    'border-green-300',
    bg:        'bg-green-50',
    text:      'text-green-700',
    badge:     'bg-green-100 text-green-700',
    connector: 'bg-green-400',
    label:     'text-green-600',
  },
  completata: {
    key:       'ppp_fase_completata',
    dot:       'bg-green-600',
    border:    'border-green-300',
    bg:        'bg-green-50',
    text:      'text-green-700',
    badge:     'bg-green-100 text-green-700',
    connector: 'bg-green-400',
    label:     'text-green-600',
  },
  sospesa: {
    key:       'ppp_fase_sospesa',
    dot:       'bg-yellow-400',
    border:    'border-yellow-300',
    bg:        'bg-yellow-50',
    text:      'text-yellow-700',
    badge:     'bg-yellow-100 text-yellow-700',
    connector: 'bg-yellow-200',
    label:     'text-yellow-600',
  },
  in_ritardo: {
    key:       'ppp_fase_in_ritardo',
    dot:       'bg-red-500',
    border:    'border-red-300',
    bg:        'bg-red-50',
    text:      'text-red-700',
    badge:     'bg-red-100 text-red-700',
    connector: 'bg-red-300',
    label:     'text-red-600',
  },
};

function FaseDot({ stato, isSelected }) {
  const cfg = STATO_CFG[stato] ?? STATO_CFG.non_avviata;
  const isAttiva = stato === 'in_corso';

  return (
    <div className="relative flex items-center justify-center">
      {isAttiva && (
        <span className="absolute inline-flex h-8 w-8 rounded-full bg-green-400 opacity-40 animate-ping" />
      )}
      <div
        className={[
          'relative flex h-8 w-8 items-center justify-center rounded-full transition-all',
          cfg.dot,
          isSelected ? 'scale-110' : 'group-hover:scale-105',
        ].join(' ')}
      >
        {(stato === 'conclusa' || stato === 'completata') && (
          <svg className="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
        {stato === 'in_corso' && (
          <span className="h-2.5 w-2.5 rounded-full bg-white block" />
        )}
      </div>
    </div>
  );
}

const EVENTO_CFG = {
  atto:         { label: 'Atto',        dot: 'bg-indigo-500',  chip: 'bg-indigo-50 text-indigo-700' },
  documento:    { label: 'Documento',   dot: 'bg-sky-500',     chip: 'bg-sky-50 text-sky-700' },
  scadenza:     { label: 'Scadenza',    dot: 'bg-amber-500',   chip: 'bg-amber-50 text-amber-700' },
  proposta:     { label: 'Proposta',    dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700' },
  integrazione: { label: 'Integrazione',dot: 'bg-purple-500',  chip: 'bg-purple-50 text-purple-700' },
};

function EventiFase({ eventi }) {
  if (!eventi || eventi.length === 0) {
    return (
      <p className="mt-4 text-sm text-gray-400 italic">
        Nessuna movimentazione registrata in questa fase.
      </p>
    );
  }

  return (
    <div className="mt-5">
      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
        Riepilogo attività della fase
      </h4>
      <ol className="relative border-l border-gray-200 ml-1.5 space-y-4">
        {eventi.map((ev, i) => {
          const cfg = EVENTO_CFG[ev.categoria] ?? { label: ev.categoria, dot: 'bg-gray-400', chip: 'bg-gray-100 text-gray-600' };
          return (
            <li key={i} className="ml-4">
              <span className={`absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full ring-2 ring-white ${cfg.dot}`} />
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${cfg.chip}`}>
                  {cfg.label}
                </span>
                {ev.data && (
                  <time className="text-xs text-gray-400">
                    {new Date(ev.data).toLocaleDateString('it-IT')}
                  </time>
                )}
              </div>
              <p className="text-sm font-medium text-gray-800 mt-0.5">{ev.titolo}</p>
              {ev.descrizione && (
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2" title={ev.descrizione}>
                  {ev.descrizione}
                </p>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function DettaglioFase({ fase, procedimentoId, canAvanza, onAggiornata, _locate }) {
  const api = usePppApi();
  const { addModal } = useStateContext();
  const [savingAvanza, setSavingAvanza] = useState(false);
  const [savingRetrocedi, setSavingRetrocedi] = useState(false);
  const cfg = STATO_CFG[fase.stato] ?? STATO_CFG.non_avviata;
  const hasDate = fase.dataInizio || fase.dataFine;

  const puoAvanzare    = canAvanza && fase.stato === 'non_avviata';
  const puoRetrocedire = canAvanza && (fase.stato === 'conclusa' || fase.stato === 'completata' || fase.stato === 'in_corso');

  const eseguiAvanza = () => {
    setSavingAvanza(true);
    api.avanzaFase(procedimentoId, fase.id, {
      onSuccess: () => { setSavingAvanza(false); onAggiornata(fase.id); },
      onError:   () => setSavingAvanza(false),
    });
  };

  const eseguiRetrocedi = () => {
    setSavingRetrocedi(true);
    api.retrocediFase(procedimentoId, fase.id, {
      onSuccess: () => { setSavingRetrocedi(false); onAggiornata(fase.id); },
      onError:   () => setSavingRetrocedi(false),
    });
  };

  const handleAvanza = () => {
    addModal(
      <Modal
        title={_locate('ppp_fase_avanza_titolo')}
        content={_locate('ppp_fase_avanza_messaggio').replace('{fase}', `${fase.numeroFase} — ${fase.nome}`)}
        confirm={_locate('ppp_fase_avanza')}
        style="success"
        onClick={eseguiAvanza}
      />
    );
  };

  const handleRetrocedi = () => {
    addModal(
      <Modal
        title={_locate('ppp_fase_retrocedi_titolo')}
        content={_locate('ppp_fase_retrocedi_messaggio').replace('{fase}', `${fase.numeroFase} — ${fase.nome}`)}
        confirm={_locate('ppp_fase_retrocedi')}
        style="warning"
        onClick={eseguiRetrocedi}
      />
    );
  };

  return (
    <div className={`w-full overflow-hidden rounded-xl border ${cfg.border} border-l-4 bg-white shadow-md transition-all`}>
      <div className="flex items-start justify-between gap-4 p-5 h-full">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap pb-3 mb-3 border-b border-gray-100">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
              Fase {fase.numeroFase}
            </span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cfg.badge}`}>
              {_locate(cfg.key)}
            </span>
          </div>
          <h3 className={`text-base font-semibold ${cfg.text}`}>{fase.nome}</h3>

          {hasDate && (
            <div className="mt-2 flex gap-4 text-sm text-gray-500">
              {fase.dataInizio && (
                <p>
                  <span className="text-xs uppercase tracking-wide text-gray-400 mr-1">Inizio</span>
                  {new Date(fase.dataInizio).toLocaleDateString('it-IT')}
                </p>
              )}
              {fase.dataFine && (
                <p>
                  <span className="text-xs uppercase tracking-wide text-gray-400 mr-1">Fine</span>
                  {new Date(fase.dataFine).toLocaleDateString('it-IT')}
                </p>
              )}
            </div>
          )}

          {!hasDate && (
            <p className="mt-2 text-sm text-gray-400 italic">Nessuna data registrata per questa fase.</p>
          )}

          <EventiFase eventi={fase.eventi} />
        </div>

        {canAvanza && (puoAvanzare || puoRetrocedire) && (
          <div className="shrink-0 flex flex-col gap-2 items-end">
            {puoAvanzare && (
              <LoadingButton
                isLoading={savingAvanza}
                customFunc={handleAvanza}
                text={_locate('ppp_fase_avanza')}
              />
            )}
            {puoRetrocedire && (
              <button
                onClick={handleRetrocedi}
                disabled={savingRetrocedi}
                className="text-xs text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
              >
                {savingRetrocedi ? '...' : _locate('ppp_fase_retrocedi')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TimelineFasi({ fasi = [], faseAttivaNumero, procedimentoId, canAvanza, onRefresh, _locate }) {
  const [selectedFase, setSelectedFase] = useState(
    fasi.find((f) => f.numeroFase === faseAttivaNumero) ?? null
  );

  // Aggiorna selectedFase se le fasi cambiano dopo un refresh
  const selectedAggiornata = selectedFase
    ? fasi.find((f) => f.id === selectedFase.id) ?? null
    : null;

  if (fasi.length === 0) {
    return (
      <div className="p-6 text-center text-gray-400 text-sm">
        Nessuna fase configurata per questo procedimento.
      </div>
    );
  }

  return (
    <div className="p-4 flex flex-col lg:flex-row lg:items-stretch gap-6">
      <div className="flex flex-col lg:w-2/5 lg:shrink-0">
        {fasi.map((fase, index) => {
          const cfg = STATO_CFG[fase.stato] ?? STATO_CFG.non_avviata;
          const isSelected = selectedAggiornata?.id === fase.id;
          const isLast = index === fasi.length - 1;

          return (
            <div key={fase.id} className="flex items-stretch gap-3">
              {/* Colonna dot + connettore verticale */}
              <div className="flex flex-col items-center">
                <FaseDot stato={fase.stato} isSelected={isSelected} />
                {!isLast && (
                  <div className={`w-0.5 flex-1 min-h-[1rem] ${cfg.connector}`} />
                )}
              </div>

              {/* Etichetta fase */}
              <button
                onClick={() => setSelectedFase(isSelected ? null : fase)}
                className="flex items-baseline gap-2 text-left group focus:outline-none pb-4 flex-1"
              >
                <span className={`text-xs font-bold leading-tight shrink-0 ${isSelected ? 'text-blue-700' : cfg.label}`}>
                  F{fase.numeroFase}
                </span>
                <span
                  className={`text-sm leading-tight ${
                    isSelected ? 'text-blue-700 font-medium' : `${cfg.text} group-hover:text-gray-700`
                  }`}
                  title={fase.nome}
                >
                  {fase.nome}
                </span>
              </button>
            </div>
          );
        })}
      </div>

      {selectedAggiornata && (
        <div className="lg:flex-1 flex border-t lg:border-t-0 lg:border-l border-gray-200 pt-6 lg:pt-0 lg:pl-6">
          <DettaglioFase
            fase={selectedAggiornata}
            procedimentoId={procedimentoId}
            canAvanza={canAvanza}
            onAggiornata={onRefresh}
            _locate={_locate}
          />
        </div>
      )}
    </div>
  );
}
